/**
 * Alert Engine — derives actionable alerts from existing operational data.
 * Pure functions only. No Supabase calls — operates on data already fetched by OpsCenter queries.
 */
import type { OpsActiveWorkflow, OpsBottleneck, OpsCriticalProblem, OpsOverdueAction } from './operationsCenterService';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AlertTipo = 'critico' | 'gargalo' | 'qualidade' | 'inatividade' | 'sla' | 'preditivo';

export interface ComputedAlert {
  id: string;
  tipo: AlertTipo;
  titulo: string;
  descricao: string;
  execution_id: string | null;
  task_id: string | null;
  stage_name: string | null;
  impacto: string;
  created_at: string;
  prioridade_score: number;
  status: 'active' | 'dismissed' | 'resolved';
  /** For grouping: how many alerts share the same tipo+stage */
  count?: number;
}

export interface AlertGroup {
  key: string;
  tipo: AlertTipo;
  stage_name: string | null;
  titulo: string;
  count: number;
  prioridade_score: number;
  topAlert: ComputedAlert;
  all: ComputedAlert[];
}

// ── Weights ───────────────────────────────────────────────────────────────────

const W_SLA = 3.0;
const W_QUEUE = 1.5;
const W_QUALITY = 0.8;
const W_INACTIVITY = 1.0;

// ── Core engine ───────────────────────────────────────────────────────────────

export function computeAlerts(input: {
  workflows: OpsActiveWorkflow[];
  bottlenecks: OpsBottleneck[];
  problems: OpsCriticalProblem[];
  overdueActions: OpsOverdueAction[];
  avgStageMinutes?: number; // org average for queue threshold
}): ComputedAlert[] {
  const { workflows, bottlenecks, problems, overdueActions } = input;
  const avgStage = input.avgStageMinutes ?? 60; // default 1h if unknown
  const now = new Date().toISOString();
  const alerts: ComputedAlert[] = [];

  // Rule 1 — SLA estourado (CRÍTICO)
  for (const wf of workflows) {
    if (wf.is_overdue) {
      const overdueMin = wf.sla_remaining_minutes != null ? Math.abs(wf.sla_remaining_minutes) : 0;
      alerts.push({
        id: `sla-${wf.id}`,
        tipo: 'sla',
        titulo: `SLA estourado — ${wf.current_stage_name ?? wf.title}`,
        descricao: `"${wf.title}" está com SLA vencido há ${fmtMin(overdueMin)}`,
        execution_id: wf.id,
        task_id: null,
        stage_name: wf.current_stage_name,
        impacto: `+${fmtMin(overdueMin)} de atraso acumulado`,
        created_at: now,
        prioridade_score: W_SLA * overdueMin,
        status: 'active',
      });
    }
    // SLA em risco (< 30min restantes) → warning level
    else if (wf.sla_remaining_minutes != null && wf.sla_remaining_minutes <= 30 && wf.sla_remaining_minutes > 0) {
      alerts.push({
        id: `sla-risk-${wf.id}`,
        tipo: 'sla',
        titulo: `SLA em risco — ${wf.current_stage_name ?? wf.title}`,
        descricao: `"${wf.title}" tem apenas ${fmtMin(wf.sla_remaining_minutes)} de SLA restante`,
        execution_id: wf.id,
        task_id: null,
        stage_name: wf.current_stage_name,
        impacto: `SLA expira em ${fmtMin(wf.sla_remaining_minutes)}`,
        created_at: now,
        prioridade_score: W_SLA * (30 - wf.sla_remaining_minutes),
        status: 'active',
      });
    }
  }

  // Rule 2 — Fila acima do dobro da média (GARGALO)
  for (const wf of workflows) {
    if (wf.waiting_minutes != null && wf.waiting_minutes > avgStage * 2) {
      alerts.push({
        id: `queue-${wf.id}`,
        tipo: 'gargalo',
        titulo: `Fila excessiva — ${wf.current_stage_name ?? 'Etapa desconhecida'}`,
        descricao: `"${wf.title}" aguarda há ${fmtMin(wf.waiting_minutes)} (média: ${fmtMin(avgStage)})`,
        execution_id: wf.id,
        task_id: null,
        stage_name: wf.current_stage_name,
        impacto: `Fila ${Math.round(wf.waiting_minutes / avgStage)}× acima da média`,
        created_at: now,
        prioridade_score: W_QUEUE * wf.waiting_minutes,
        status: 'active',
      });
    }
  }

  // Rule 3 — Gargalos críticos de etapa
  for (const bn of bottlenecks) {
    const impactScore = bn.stuck_count * bn.avg_minutes;
    if (impactScore > 200) {
      alerts.push({
        id: `bottleneck-${bn.stage_name}`,
        tipo: 'gargalo',
        titulo: `Gargalo crítico — ${bn.stage_name}`,
        descricao: `${bn.stuck_count} tarefa${bn.stuck_count !== 1 ? 's' : ''} parada${bn.stuck_count !== 1 ? 's' : ''} há ~${fmtMin(bn.avg_minutes)} em média`,
        execution_id: null,
        task_id: null,
        stage_name: bn.stage_name,
        impacto: `+${fmtMin(bn.avg_minutes * bn.stuck_count)} lead time acumulado`,
        created_at: now,
        prioridade_score: W_QUEUE * impactScore / 100,
        status: 'active',
      });
    }
  }

  // Rule 4 — Problema crítico aberto
  for (const p of problems) {
    if (p.severity === 'critical') {
      const openMin = Math.round((Date.now() - new Date(p.created_at).getTime()) / 60000);
      alerts.push({
        id: `problem-${p.id}`,
        tipo: 'critico',
        titulo: `Problema crítico — ${p.title}`,
        descricao: p.department ? `Área: ${p.department} · Aberto há ${fmtMin(openMin)}` : `Aberto há ${fmtMin(openMin)}`,
        execution_id: null,
        task_id: null,
        stage_name: null,
        impacto: 'Alto impacto operacional',
        created_at: p.created_at,
        prioridade_score: W_SLA * openMin,
        status: 'active',
      });
    }
  }

  // Rule 5 — Ações muito atrasadas (inatividade > 3 dias)
  for (const a of overdueActions) {
    if (a.days_overdue >= 3) {
      alerts.push({
        id: `action-${a.id}`,
        tipo: 'inatividade',
        titulo: `Ação parada — ${a.title}`,
        descricao: `Sem atualização há ${a.days_overdue} dias${a.project_name ? ` · ${a.project_name}` : ''}`,
        execution_id: null,
        task_id: null,
        stage_name: null,
        impacto: `Atraso acumulado de ${a.days_overdue}d`,
        created_at: a.due_date,
        prioridade_score: W_INACTIVITY * a.days_overdue * 60,
        status: 'active',
      });
    }
  }

  return alerts.sort((a, b) => b.prioridade_score - a.prioridade_score);
}

// ── Predictive engine ────────────────────────────────────────────────────────

/**
 * Compares current vs previous bottleneck snapshot to detect queue growth trend.
 * Fires "⚠️ Risco de gargalo em ~Xh" when:
 *   - stuck_count grew by ≥2 tasks OR grew by ≥50% in one polling cycle
 *   - AND avg_minutes is already above a base threshold (stage is not trivially fast)
 *   - AND estimated saturation time (capacity exhaustion) < 4h
 */
export function computePredictiveAlerts(
  current: OpsBottleneck[],
  previous: OpsBottleneck[],
  avgStageMinutes = 60,
): ComputedAlert[] {
  if (previous.length === 0) return [];

  const now = new Date().toISOString();
  const alerts: ComputedAlert[] = [];

  for (const cur of current) {
    const prev = previous.find(p => p.stage_name === cur.stage_name);
    if (!prev) continue; // new stage — not enough history

    const delta = cur.stuck_count - prev.stuck_count;
    const growthRate = prev.stuck_count > 0 ? delta / prev.stuck_count : 0;

    // Only fire if queue is genuinely growing (≥2 tasks added OR ≥50% growth)
    // AND stage avg is above base (non-trivial stage)
    if (delta < 2 && growthRate < 0.5) continue;
    if (cur.avg_minutes < 15) continue; // trivially fast stage, not worth alerting

    // Estimate time-to-saturation:
    // Assume each task takes avg_minutes to process, one-at-a-time capacity.
    // At the current delta rate per 15s polling cycle, estimate when it overflows.
    // Conservative: assume delta tasks/cycle, 4 cycles/min → tasks per hour from queue
    const tasksPerHour = delta * 4; // delta tasks arrived in last 15s → per minute * 60
    const processingCapacity = Math.max(1, Math.round(60 / cur.avg_minutes)); // tasks/hour at single-thread
    const netAccumulation = tasksPerHour - processingCapacity; // tasks accumulating per hour

    if (netAccumulation <= 0) continue; // not actually growing faster than processing

    // Hours until the queue becomes critical (stuck_count doubles)
    const hoursToSaturation = netAccumulation > 0
      ? Math.round((cur.stuck_count / netAccumulation) * 10) / 10
      : 99;

    if (hoursToSaturation > 4) continue; // too far out to alert

    const timeLabel = hoursToSaturation < 1
      ? `${Math.round(hoursToSaturation * 60)}min`
      : `${hoursToSaturation}h`;

    alerts.push({
      id: `preditivo-${cur.stage_name}`,
      tipo: 'preditivo',
      titulo: `⚠️ Risco de gargalo em ~${timeLabel} — ${cur.stage_name}`,
      descricao: `Fila cresceu +${delta} tarefa${delta !== 1 ? 's' : ''} desde a última leitura. Capacidade de processamento insuficiente para absorver o volume atual.`,
      execution_id: null,
      task_id: null,
      stage_name: cur.stage_name,
      impacto: `Saturação estimada em ~${timeLabel} se o ritmo continuar`,
      created_at: now,
      prioridade_score: 80 / Math.max(0.1, hoursToSaturation), // closer = higher priority
      status: 'active',
    });
  }

  return alerts.sort((a, b) => b.prioridade_score - a.prioridade_score);
}

// ── Grouping ──────────────────────────────────────────────────────────────────

export function groupAlerts(alerts: ComputedAlert[]): AlertGroup[] {
  const map = new Map<string, ComputedAlert[]>();
  for (const a of alerts) {
    const key = `${a.tipo}::${a.stage_name ?? '_'}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(a);
  }
  const groups: AlertGroup[] = [];
  for (const [key, group] of map.entries()) {
    const top = group[0];
    const count = group.length;
    groups.push({
      key,
      tipo: top.tipo,
      stage_name: top.stage_name,
      titulo: count > 1 ? buildGroupTitle(top.tipo, count, top.stage_name) : top.titulo,
      count,
      prioridade_score: group.reduce((s, a) => s + a.prioridade_score, 0),
      topAlert: top,
      all: group,
    });
  }
  return groups.sort((a, b) => b.prioridade_score - a.prioridade_score);
}

function buildGroupTitle(tipo: AlertTipo, count: number, stage: string | null): string {
  switch (tipo) {
    case 'sla': return `${count} tarefas com SLA estourado${stage ? ` em "${stage}"` : ''}`;
    case 'gargalo': return `${count} gargalos críticos${stage ? ` em "${stage}"` : ''}`;
    case 'critico': return `${count} problemas críticos ativos`;
    case 'inatividade': return `${count} ações paradas sem atualização`;
    case 'preditivo': return `${count} riscos preditivos de gargalo`;
    default: return `${count} alertas de ${tipo}`;
  }
}

// ── Alert Log (localStorage — enterprise audit trail) ─────────────────────────

const LOG_KEY = 'ops_alert_log';
const MAX_LOG = 200;

export interface AlertLogEntry {
  alert_id: string;
  tipo: AlertTipo;
  event: 'generated' | 'clicked' | 'dismissed' | 'resolved';
  timestamp: string;
  resolution_minutes?: number;
}

export function logAlertEvent(entry: Omit<AlertLogEntry, 'timestamp'>): void {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    const log: AlertLogEntry[] = raw ? JSON.parse(raw) : [];
    log.unshift({ ...entry, timestamp: new Date().toISOString() });
    if (log.length > MAX_LOG) log.length = MAX_LOG;
    localStorage.setItem(LOG_KEY, JSON.stringify(log));
  } catch {
    // localStorage unavailable in some Electron contexts — fail silently
  }
}

export function getAlertLog(): AlertLogEntry[] {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMin(minutes: number): string {
  const m = Math.round(Math.abs(minutes));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}
